import { v4 as uuidv4 } from 'uuid'
import { isConcreteStep, type Element, type Step } from '../../../../shared/domain/flows'
import { assumeExhaustion, type UUID } from '../../../../shared/lib/typeHelpers.no-deps'
import { hasShowIfs } from '../../../../shared/domain/flows/typeGuards.no-deps'
import type { FlowItem } from '../../../../shared/domain/viewableReports/types.no-deps'
import type { LoopPath } from '../../../../shared/domain/flows/LoopPath.no-deps'

type SectionGrouping = {
    id: UUID
    labels: string[]
    steps: StepGrouping[]
    loopPath: LoopPath
}

export type TopLevelStepGrouping = StepGrouping & {
    sectionGroupings: SectionGrouping[]
}

type StepGrouping = {
    id: UUID
    label: string
    items: FlowItem[]
    loopPath: LoopPath
}

interface Output {
    stepGroupings: TopLevelStepGrouping[]
    remainingItems: FlowItem[]
}

const getSectionsAndStepsFromElements = (elements: Element[], items: FlowItem[], loopPath: LoopPath) => {
    let stepItems: FlowItem[] = []
    let stepSectionGroupings: SectionGrouping[] = []
    let stepGroupings: TopLevelStepGrouping[] = []
    let remainingItems: FlowItem[] = [...items]

    elements.forEach((element) => {
        switch (element.type) {
            case 'loop':
                const loopOutput = getSectionGroupings(element.steps, remainingItems, loopPath)
                remainingItems = loopOutput.remainingItems
                stepGroupings = [...stepGroupings, ...loopOutput.stepGroupings]

                element.unitFilters.forEach((unitFilter) => {
                    if (unitFilter.type === 'ParentIsUserSelectable') {
                        const elementItems = remainingItems.filter((item) => {
                            switch (item.type) {
                                case 'element':
                                    return item.element.id === unitFilter.id
                                case 'answer':
                                    if (item.answer.value.type === 'calculation') {
                                        return false
                                    }
                                    return item.answer.value.rawAnswer.element === unitFilter.id
                                default:
                                    assumeExhaustion(item)
                            }
                        })

                        elementItems.forEach((elementItem) => {
                            stepItems.push(elementItem)
                            remainingItems = remainingItems.filter((item) => {
                                if (item.type === 'answer' && item.answer.value.type === 'calculation') {
                                    return true
                                }
                                if (elementItem.type === 'answer' && elementItem.answer.value.type === 'calculation') {
                                    return true
                                }
                                return item !== elementItem
                            })
                        })
                    }
                })
                break
            case 'section':
                const nestedSectionOutput = getSectionGroupings(element.steps, remainingItems, loopPath)
                remainingItems = nestedSectionOutput.remainingItems
                if (nestedSectionOutput.stepGroupings.length > 0) {
                    const sectionStepGroupings: StepGrouping[] = []
                    nestedSectionOutput.stepGroupings.forEach(({ sectionGroupings, ...nestedStepGrouping }) => {
                        sectionStepGroupings.push(nestedStepGrouping)
                        sectionGroupings.forEach((nestedGrouping) => {
                            stepSectionGroupings.push({
                                ...nestedGrouping,
                                labels: [element.label, ...nestedGrouping.labels],
                            })
                        })
                    })
                    stepSectionGroupings = [
                        {
                            id: uuidv4() as UUID,
                            labels: [element.label],
                            steps: sectionStepGroupings,
                            loopPath,
                        },
                        ...stepSectionGroupings,
                    ]
                }
                break
            default:
                const elementItems = remainingItems.filter((item) => {
                    // Calculation answers don't have a raw element so we can't relate them back
                    // to the original flow structure. In this case calculations will be left in the
                    // remainingAnswers object and the consumer of this should fall back to the
                    // legacy way of rendering reports
                    switch (item.type) {
                        case 'element':
                            return item.element.id === element.id
                        case 'answer':
                            if (item.answer.value.type === 'calculation') {
                                return false
                            }
                            return item.answer.value.rawAnswer.element === element.id
                        default:
                            assumeExhaustion(item)
                    }
                })

                elementItems.forEach((elementItem) => {
                    stepItems.push(elementItem)
                    remainingItems = remainingItems.filter((item) => {
                        // Calculation answers don't have a raw element so we can't relate them back
                        // to the original flow structure. In this case calculations will be left in the
                        // remainingAnswers object and the consumer of this should fall back to the
                        // legacy way of rendering reports
                        if (item.type === 'answer' && item.answer.value.type === 'calculation') {
                            return true
                        }
                        if (elementItem.type === 'answer' && elementItem.answer.value.type === 'calculation') {
                            return true
                        }
                        return item !== elementItem
                    })
                })

                if (hasShowIfs(element)) {
                    if (element.showIfs) {
                        const showIfElements = element.showIfs.reduce<Element[]>(
                            (output, showIf) => [...output, ...showIf.elements],
                            []
                        )
                        const showIfStepSummary = getSectionsAndStepsFromElements(
                            showIfElements,
                            remainingItems,
                            loopPath
                        )
                        remainingItems = showIfStepSummary.remainingItems
                        stepItems = [...stepItems, ...showIfStepSummary.items]
                        stepGroupings = [...stepGroupings, ...showIfStepSummary.stepGroupings]
                        stepSectionGroupings = [...stepSectionGroupings, ...showIfStepSummary.sectionGroupings]
                    }
                }
        }
    })

    return {
        items: stepItems,
        stepGroupings,
        sectionGroupings: stepSectionGroupings,
        remainingItems,
    }
}

export const getSectionGroupings = (steps: Step[], items: FlowItem[], loopPath: LoopPath): Output => {
    let stepGroupings: TopLevelStepGrouping[] = []
    let remainingItems = Array.isArray(items) ? items : []

    remainingItems = remainingItems.filter((item) => item.type === 'element' || item.answer.value.type !== 'section')

    steps.forEach((step) => {
        if (!isConcreteStep(step)) {
            return
        }
        if (step.elements.length !== 0) {
            const stepGrouping: TopLevelStepGrouping = {
                id: uuidv4() as UUID,
                label: step.label,
                items: [],
                sectionGroupings: [],
                loopPath,
            }

            const stepSummary = getSectionsAndStepsFromElements(step.elements, remainingItems, loopPath)
            remainingItems = stepSummary.remainingItems
            stepGrouping.items = stepSummary.items
            stepGrouping.sectionGroupings = stepSummary.sectionGroupings
            stepGroupings = [...stepGroupings, ...stepSummary.stepGroupings]

            if (stepGrouping.items.length > 0 || stepGrouping.sectionGroupings.length > 0) {
                stepGroupings.push(stepGrouping)
            }
        }
    })

    return {
        stepGroupings,
        remainingItems,
    }
}
